/**
 * JarvisJoin — página pública mobile-first para audiência de uma sessão Jarvis.
 * Acessível via QR Code: /join/:token
 *  - Valida guest_token contra jarvis-session (action=validate_guest_token)
 *  - Mostra event_name + transcript em tempo real (read-only)
 *  - Permite enviar pergunta se allow_guest_questions=true
 */
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Mic, AlertTriangle, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

type Validation = {
  valid: boolean;
  session_id: string;
  event_name: string | null;
  status: string;
  allow_guest_questions: boolean;
};

type Transcript = {
  id: string;
  text: string;
  speaker_label: string | null;
  is_final: boolean;
  created_at: string;
};

export default function JarvisJoin() {
  const { token } = useParams<{ token: string }>();
  const [validation, setValidation] = useState<Validation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [joined, setJoined] = useState(false);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [question, setQuestion] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Valida token
  useEffect(() => {
    if (!token) return;
    void (async () => {
      const { data, error: err } = await supabase.functions.invoke("jarvis-session", {
        body: { action: "validate_guest_token", guest_token: token },
      });
      if (err || !data?.valid) {
        setError(data?.error || err?.message || "Token inválido ou expirado");
      } else {
        setValidation(data as Validation);
      }
      setLoading(false);
    })();
  }, [token]);

  // Realtime: assina transcripts da sessão
  useEffect(() => {
    if (!joined || !validation?.session_id) return;
    const sid = validation.session_id;

    void (async () => {
      const { data } = await supabase
        .from("jarvis_transcripts")
        .select("id, text, speaker_label, is_final, created_at")
        .eq("session_id", sid)
        .eq("is_final", true)
        .order("created_at", { ascending: true })
        .limit(50);
      setTranscripts((data ?? []) as Transcript[]);
    })();

    const ch = supabase
      .channel(`join-transcripts-${sid}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "jarvis_transcripts", filter: `session_id=eq.${sid}` },
        (payload) => {
          const t = payload.new as Transcript;
          if (t.is_final) setTranscripts((prev) => [...prev.slice(-49), t]);
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(ch);
    };
  }, [joined, validation?.session_id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [transcripts.length]);

  const handleJoin = () => {
    if (!displayName.trim()) {
      toast.error("Informe seu nome");
      return;
    }
    setJoined(true);
  };

  const handleSendQuestion = async () => {
    if (!question.trim() || !validation) return;
    setSending(true);
    try {
      const { error: err } = await supabase.functions.invoke("jarvis-session", {
        body: {
          action: "submit_guest_question",
          guest_token: token,
          question: question.trim(),
          display_name: displayName,
        },
      });
      if (err) throw err;
      toast.success("Pergunta enviada");
      setQuestion("");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Erro: ${msg}`);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !validation) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" /> Acesso negado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{error ?? "Token inválido."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!joined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-primary" />
              {validation.event_name ?? "Sessão Jarvis"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Você foi convidado para participar como audiência. Informe seu nome para entrar.
            </p>
            <Input
              placeholder="Seu nome"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleJoin()}
              maxLength={60}
              autoFocus
            />
            <Button onClick={handleJoin} className="w-full" size="lg">
              Entrar na sessão
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-2xl mx-auto">
      <header className="px-4 py-3 border-b border-border bg-card sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            <h1 className="text-sm font-semibold truncate font-headline">
              {validation.event_name ?? "Sessão Jarvis"}
            </h1>
            <p className="text-[11px] text-muted-foreground">
              {validation.status} · {displayName}
            </p>
          </div>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {transcripts.length === 0 ? (
          <div className="text-center text-muted-foreground text-sm py-12">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            Aguardando início da transmissão…
          </div>
        ) : (
          transcripts.map((t) => (
            <div key={t.id} className="rounded-lg border border-border p-3 bg-card">
              <div className="text-[10px] text-muted-foreground mb-1 uppercase tracking-wide">
                {t.speaker_label ?? "fala"}
              </div>
              <p className="text-sm text-foreground">{t.text}</p>
            </div>
          ))
        )}
      </div>

      {validation.allow_guest_questions && (
        <div className="border-t border-border p-3 bg-card sticky bottom-0">
          <div className="flex gap-2">
            <Input
              placeholder="Faça uma pergunta…"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendQuestion()}
              maxLength={500}
              disabled={sending}
            />
            <Button onClick={handleSendQuestion} disabled={sending || !question.trim()} size="icon">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
